import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import {
  ArrowLeft,
  Wand2,
  Plus,
  Minus,
  Calendar,
  User,
  Dumbbell
} from 'lucide-react'
import { useProgramStore } from '@/stores/programStore'
import { LibraryService, apiService, Client } from '@/services'
import { ProgramGenerationRequest, MuscleActivationData, Equipment } from '@/types'
import { LoadingSpinner } from '@/components/ui/LoadingSpinner'
import toast from 'react-hot-toast'

const programGenerationSchema = z.object({
  userId: z.string().min(1, 'Client selection is required'),
  programLength: z.number().min(1).max(52, 'Program length must be between 1-52 weeks'),
  startDate: z.string().min(1, 'Start date is required'),
  endDate: z.string().optional(),
  schedule: z.array(z.number()).min(1, 'At least one workout day is required'),
  totalWeeklySets: z.number().min(10).max(200, 'Weekly sets must be between 10-200'),
  gender: z.enum(['male', 'female', 'other']),
  fitnessLevel: z.enum(['beginner', 'intermediate', 'advanced']).optional(),
  goals: z.array(z.string()).optional(),
  equipment: z.array(z.string()).optional(),
  injuries: z.array(z.string()).optional(),
  preferences: z.object({
    workoutDuration: z.number().optional(),
    intensityPreference: z.enum(['low', 'moderate', 'high']).optional(),
    exerciseVariety: z.enum(['minimal', 'moderate', 'high']).optional(),
  }).optional()
})

type ProgramGenerationForm = z.infer<typeof programGenerationSchema>

interface ProgramCreateViewProps {
  onBack: () => void
}

export function ProgramCreateView({ onBack }: ProgramCreateViewProps) {
  const navigate = useNavigate()
  const {
    isGenerating,
    generationProgress,
    muscleActivations,
    setMuscleActivations,
    setIsGenerating,
    setGenerationProgress
  } = useProgramStore()

  const [selectedMuscleActivations, setSelectedMuscleActivations] = useState<Record<string, number>>({})
  const [customGoals, setCustomGoals] = useState<string[]>([])
  const [customEquipment, setCustomEquipment] = useState<Equipment[]>([])
  const [step, setStep] = useState(1)
  const [availableEquipment, setAvailableEquipment] = useState<Array<{id: string, name: string}>>([])
  const [loadingEquipment, setLoadingEquipment] = useState(false)
  const [availableClients, setAvailableClients] = useState<Client[]>([])
  const [loadingClients, setLoadingClients] = useState(false)

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    trigger,
    formState: { errors, isValid }
  } = useForm<ProgramGenerationForm>({
    resolver: zodResolver(programGenerationSchema),
    defaultValues: {
      userId: '', // Will be set when clients load
      programLength: 8,
      startDate: new Date().toISOString().split('T')[0], // String format for date input
      schedule: [1, 3, 5], // Monday, Wednesday, Friday
      totalWeeklySets: 12,
      gender: 'male',
      fitnessLevel: 'intermediate',
      preferences: {
        workoutDuration: 60,
        intensityPreference: 'moderate',
        exerciseVariety: 'moderate'
      }
    },
    mode: 'onChange' // This will validate on every change, making isValid more responsive
  })

  const watchedSchedule = watch('schedule')
  const watchedProgramLength = watch('programLength')
  const watchedStartDate = watch('startDate')
  


  // Calculate expected end date
  const calculateEndDate = (startDate: Date, weeks: number): Date | undefined => {
    if (!startDate) return undefined
    const endDate = new Date(startDate)
    endDate.setDate(endDate.getDate() + (weeks * 7))
    return endDate
  }

  useEffect(() => {
    const initializeComponent = async () => {
      try {
        await Promise.all([
          loadMuscleActivations(),
          loadEquipmentOptions(),
          loadClientsOptions()
        ])
        
        // Trigger validation after component mounts
        setTimeout(() => {
          trigger() // Trigger validation for entire form
        }, 100)
      } catch (error) {
        console.error('❌ Component initialization failed:', error)
        // Error is already shown via toast in individual load functions
        // Component will show error state instead of crashing
      }
    }
    
    initializeComponent()
  }, [])

  const loadMuscleActivations = async () => {
    try {
      const result = await LibraryService.getMuscleGroups()
      const muscleGroups = result.data
      
      // Convert muscle groups to MuscleActivationData format
      const activations: MuscleActivationData[] = muscleGroups.map(mg => ({
        id: mg.id,
        name: mg.name,
        scientificName: mg.description,
        category: 'primary', // Default category
        activation: 0.5
      }))
      
      setMuscleActivations(activations)
      
      // Set default muscle activations
      const defaultActivations: Record<string, number> = {}
      activations.forEach(muscle => {
        defaultActivations[muscle.id] = 0.5
      })
      setSelectedMuscleActivations(defaultActivations)
    } catch (error) {
      console.error('Failed to load muscle activations:', error)
      toast.error('Failed to load muscle data')
    }
  }

  const loadEquipmentOptions = async () => {
    try {
      setLoadingEquipment(true)
      const result = await LibraryService.getEquipment({}, 1, 100)
      const equipment = result.data.map(eq => ({
        id: eq.id,
        name: eq.name
      }))
      setAvailableEquipment(equipment)
    } catch (error) {
      console.error('Failed to load equipment:', error)
      toast.error('Failed to load equipment data')
    } finally {
      setLoadingEquipment(false)
    }
  }

  const loadClientsOptions = async () => {
    // Prevent duplicate calls
    if (loadingClients) {
      console.log('⏳ Client options already loading, skipping duplicate call')
      return
    }
    
    try {
      setLoadingClients(true)
      
      // Load only coach's assigned clients for proper filtering
      console.log('🔄 Loading coach clients from backend...')
      
      // Try to get current coach profile and their clients
      try {
        console.log('🔄 Getting current coach profile...')
        const coachProfileResponse = await apiService.getCoachProfile()
        
        if (coachProfileResponse.success && coachProfileResponse.data) {
          const coachId = (coachProfileResponse.data as any).id
          console.log(`✅ Found coach ID: ${coachId}`)
          
          const coachResponse = await apiService.getCoachClients({ limit: 100, coachId })
          if (coachResponse.success && coachResponse.data) {
            const clients = Array.isArray(coachResponse.data) ? coachResponse.data : (coachResponse.data as any).clients || []
            if (clients.length > 0) {
              console.log(`✅ Loaded ${clients.length} coach clients (filtered for this coach)`)
              setAvailableClients(clients)
              setValue('userId', clients[0].id)
              trigger('userId')
              return
            } else {
              throw new Error(`No clients assigned to this coach. Please assign clients first.`)
            }
          }
        }
      } catch (coachError) {
        console.warn('❌ Coach profile/clients API failed, falling back to all clients:', coachError)
      }
      
      // Fallback to all clients if coach API fails
      const response = await apiService.getClients({ limit: 100 })
      
      if (response.success && response.data) {
        const clients = Array.isArray(response.data) ? response.data : response.data.clients || []
        setAvailableClients(clients)
        
        console.log(`✅ Loaded ${clients.length} clients successfully:`, clients.map(c => ({ 
          id: c.id, 
          name: `${c.user?.firstName || ''} ${c.user?.lastName || ''}`.trim() || 'Unnamed Client',
          email: c.user?.email 
        })))
        
        // Auto-select first client to make form valid
        if (clients.length > 0) {
          setValue('userId', clients[0].id)
          trigger('userId') // Trigger validation for userId field
        } else {
          throw new Error('No clients found in the system. Please add clients first.')
        }
      } else {
        console.error('❌ Failed to load clients - API response:', response)
        throw new Error(`Failed to load clients: ${response.error || 'Unknown API error'}`)
      }
    } catch (error) {
      console.error('❌ Client loading failed:', error)
      
      // Show real error to user - NO MOCK DATA
      const errorMessage = error instanceof Error ? error.message : 'Failed to load clients'
      toast.error(`Client Loading Error: ${errorMessage}`)
      
      // Set empty clients array so user sees the error
      setAvailableClients([])
      
      // Don't hide the error with mock data - let user see what's actually wrong
      throw error
    } finally {
      setLoadingClients(false)
    }
  }

  const handleScheduleChange = (day: number, checked: boolean) => {
    const currentSchedule = watchedSchedule || []
    let newSchedule: number[]
    
    if (checked) {
      newSchedule = [...currentSchedule, day].sort()
    } else {
      newSchedule = currentSchedule.filter(d => d !== day)
    }
    
    setValue('schedule', newSchedule, { shouldValidate: true })
  }

  const handleMuscleActivationChange = (muscleId: string, activation: number) => {
    setSelectedMuscleActivations(prev => ({
      ...prev,
      [muscleId]: activation
    }))
  }

  const addCustomGoal = (goal: string) => {
    if (goal.trim() && !customGoals.includes(goal.trim())) {
      setCustomGoals(prev => [...prev, goal.trim()])
    }
  }

  const removeCustomGoal = (goal: string) => {
    setCustomGoals(prev => prev.filter(g => g !== goal))
  }

  const addCustomEquipment = (equipment: Equipment) => {
    if (!customEquipment.includes(equipment)) {
      setCustomEquipment(prev => [...prev, equipment])
    }
  }

  const removeCustomEquipment = (equipment: Equipment) => {
    setCustomEquipment(prev => prev.filter(e => e !== equipment))
  }

  // Injury management functions - commented out as not currently used
  // const addCustomInjury = (injury: string) => {
  //   if (injury.trim() && !customInjuries.includes(injury.trim())) {
  //     setCustomInjuries(prev => [...prev, injury.trim()])
  //   }
  // }

  // const removeCustomInjury = (injury: string) => {
  //   setCustomInjuries(prev => prev.filter(i => i !== injury))
  // }

  const onSubmit = async (data: ProgramGenerationForm) => {
    // Convert string dates to Date objects (moved outside try block for scope)
    const startDate = new Date(data.startDate)
    const endDate = data.endDate ? new Date(data.endDate) : calculateEndDate(startDate, data.programLength)
    
    try {

      const request: ProgramGenerationRequest = {
        ...data,
        startDate,
        endDate,
        muscleActivations: selectedMuscleActivations,
        goals: customGoals,
        equipment: customEquipment,
        injuries: [] // Empty for now - can be added later
      }

      setIsGenerating(true)
      setGenerationProgress(0)

      // Simulate progress updates
      let currentProgress = 0
      const progressInterval = setInterval(() => {
        currentProgress = currentProgress < 90 ? currentProgress + 10 : currentProgress
        setGenerationProgress(currentProgress)
      }, 500)

      // Call the ENHANCED program generation API
      console.log('🚀 Calling ENHANCED program generation with database-backed libraries...')
      
      // Use enhanced API service for database-driven generation
      const { enhancedApiService } = await import('@/services/enhancedApi')
      const enhancedRequest = {
        clientId: request.userId, // Map userId to clientId
        name: `Program for ${availableClients.find(c => c.id === request.userId)?.user?.firstName || 'Client'}`,
        description: `Generated enhanced program with ${request.totalWeeklySets} weekly sets`,
        schedule: request.schedule,
        totalWeeklySets: request.totalWeeklySets,
        muscleGroupPreferences: request.muscleActivations,
        programLength: request.programLength,
        gender: request.gender === 'other' ? 'male' : request.gender,
        fitnessGoal: 'hypertrophy' as const, // Default for now
        experienceLevel: request.fitnessLevel || 'intermediate',
        availableEquipment: [],
        excludedExercises: []
      }
      
      const enhancedResponse = await enhancedApiService.generateEnhancedProgram(enhancedRequest)
      const generatedProgram = {
        ...enhancedResponse.data.program,
        id: enhancedResponse.data.programId  // Attach the database program ID
      }

      // Clear interval and complete
      clearInterval(progressInterval)
      setGenerationProgress(100)

      toast.success('Program generated successfully!')

      // Navigate to day assignment after a brief delay
      setTimeout(() => {
        setIsGenerating(false)
        setGenerationProgress(0)

        // Navigate to day assignment view with program details
        const assignmentParams = new URLSearchParams({
          programId: enhancedResponse.data.programId,  // Use programId from response
          startDate: startDate.toISOString(),
          endDate: (endDate || calculateEndDate(startDate, data.programLength))?.toISOString() || '',
          programLength: data.programLength.toString(),
          schedule: JSON.stringify(data.schedule) // Add schedule to URL params
        })
        
        // Pass the generated program data and form data via navigation state
        navigate(`/programs/assign-days?${assignmentParams}`, {
          state: { 
            generatedProgram: {
              ...generatedProgram,
              schedule: data.schedule, // Ensure schedule is included
              originalFormData: data // Include all form data
            }
          }
        })
      }, 1000)
      
    } catch (error: any) {
      console.error('Program generation failed:', error)
      setIsGenerating(false)
      setGenerationProgress(0)
      
      // Create a fallback program for error cases
      const fallbackProgram = {
        id: `fallback-program-${Date.now()}`,
        name: 'Generated Program',
        description: 'Fallback program due to service unavailable',
        status: 'DRAFT' as const,
        totalWeeks: data.programLength,
        goals: customGoals,
        createdAt: new Date().toISOString(),
        clientId: data.userId
      }
      
      // Provide specific error messages based on error type but still navigate
      if (error?.message?.includes('Request failed with status code 503') || error?.response?.status === 503) {
        toast.success('Service temporarily unavailable - using sample program!')
        
        // Navigate to assignment view with fallback data
        setTimeout(() => {
          const assignmentParams = new URLSearchParams({
            programId: fallbackProgram.id,
            startDate: startDate.toISOString(),
            endDate: (endDate || calculateEndDate(startDate, data.programLength))?.toISOString() || '',
            programLength: data.programLength.toString(),
            schedule: JSON.stringify(data.schedule) // Add schedule to URL params
          })
          
          navigate(`/programs/assign-days?${assignmentParams}`, {
            state: { 
              generatedProgram: {
                ...fallbackProgram,
                schedule: data.schedule, // Ensure schedule is included
                originalFormData: data // Include all form data
              },
              isFallback: true
            }
          })
        }, 1000)
      } else {
        toast.error('Failed to generate program. Please try again.')
        
        // Still provide option to continue with sample data
        setTimeout(() => {
          const shouldContinue = confirm('Would you like to continue with sample workout data?')
          if (shouldContinue) {
            const assignmentParams = new URLSearchParams({
              programId: fallbackProgram.id,
              startDate: startDate.toISOString(),
              endDate: (endDate || calculateEndDate(startDate, data.programLength))?.toISOString() || '',
              programLength: data.programLength.toString(),
              schedule: JSON.stringify(data.schedule) // Add schedule to URL params
            })
            
            navigate(`/programs/assign-days?${assignmentParams}`, {
              state: { 
                generatedProgram: {
                  ...fallbackProgram,
                  schedule: data.schedule, // Ensure schedule is included
                  originalFormData: data // Include all form data
                },
                isFallback: true
              }
            })
          }
        }, 1000)
      }
    }
  }

  const dayNames = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun']

  if (isGenerating) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center space-y-4">
          <div className="w-16 h-16 mx-auto">
            <LoadingSpinner size="lg" />
          </div>
          <div>
            <h3 className="text-lg font-medium text-gray-900">Generating Your Program</h3>
            <p className="text-gray-600 mt-1">
              Creating a personalized workout plan...
            </p>
          </div>
          <div className="w-64 bg-gray-200 rounded-full h-3 mx-auto">
            <div 
              className="bg-blue-500 h-3 rounded-full transition-all duration-500"
              style={{ width: `${generationProgress}%` }}
            />
          </div>
          <p className="text-sm text-gray-500">{generationProgress}% complete</p>
        </div>
      </div>
    )
  }

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <button
          onClick={onBack}
          className="p-2 text-gray-400 hover:text-gray-600"
        >
          <ArrowLeft className="w-5 h-5" />
        </button>
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Create Program</h1>
          <p className="text-gray-600">
            Generate a personalized workout program using AI
          </p>
        </div>
      </div>

      {/* Progress Steps */}
      <div className="flex items-center justify-center space-x-4">
        {[1, 2, 3].map((stepNumber) => (
          <div key={stepNumber} className="flex items-center">
            <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
              step >= stepNumber 
                ? 'bg-blue-500 text-white' 
                : 'bg-gray-200 text-gray-500'
            }`}>
              {stepNumber}
            </div>
            {stepNumber < 3 && (
              <div className={`w-16 h-1 mx-2 ${
                step > stepNumber ? 'bg-blue-500' : 'bg-gray-200'
              }`} />
            )}
          </div>
        ))}
      </div>

      {/* Form */}
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-8">
        {/* Step 1: Basic Information */}
        {step === 1 && (
          <div className="coach-section space-y-6">
            <h2 className="text-xl font-semibold text-gray-900 flex items-center">
              <User className="w-5 h-5 mr-2" />
              Basic Information
            </h2>

            {/* Client Selection */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Client *
              </label>
              {loadingClients ? (
                <div className="flex items-center justify-center p-3 border border-gray-300 rounded-lg">
                  <LoadingSpinner size="sm" />
                  <span className="ml-2 text-sm text-gray-600">Loading clients...</span>
                </div>
              ) : (
                <select
                  {...register('userId')}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">Select a client</option>
                  {availableClients.map((client) => (
                    <option key={client.id} value={client.id}>
                      {client.user ? 
                        `${client.user.firstName || ''} ${client.user.lastName || ''}`.trim() || client.user.email 
                        : `Client ${client.id}`
                      }
                      {client.tier && ` (${client.tier})`}
                    </option>
                  ))}
                </select>
              )}
              {availableClients.length === 0 && !loadingClients && (
                <p className="text-sm text-gray-500 mt-1">
                  No clients available. Please add clients to your coaching roster first.
                </p>
              )}
              {errors.userId && (
                <p className="mt-1 text-sm text-red-600">{errors.userId.message}</p>
              )}
            </div>

            {/* Gender */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Gender *
              </label>
              <div className="flex gap-4">
                {['male', 'female', 'other'].map((gender) => (
                  <label key={gender} className="flex items-center">
                    <input
                      type="radio"
                      value={gender}
                      {...register('gender')}
                      className="mr-2"
                    />
                    {gender.charAt(0).toUpperCase() + gender.slice(1)}
                  </label>
                ))}
              </div>
            </div>

            {/* Fitness Level */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Fitness Level
              </label>
              <select
                {...register('fitnessLevel')}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              >
                <option value="beginner">Beginner</option>
                <option value="intermediate">Intermediate</option>
                <option value="advanced">Advanced</option>
              </select>
            </div>

            {/* Program Length */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Program Duration (weeks) *
              </label>
              <input
                type="number"
                min="1"
                max="52"
                {...register('programLength', { valueAsNumber: true })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              />
              {errors.programLength && (
                <p className="mt-1 text-sm text-red-600">{errors.programLength.message}</p>
              )}
            </div>

            {/* Start Date */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Start Date *
              </label>
              <input
                type="date"
                defaultValue={new Date().toISOString().split('T')[0]}
                {...register('startDate')}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              />
              {errors.startDate && (
                <p className="mt-1 text-sm text-red-600">{errors.startDate.message}</p>
              )}
            </div>

            {/* End Date (Optional) */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                End Date (optional)
              </label>
              <input
                type="date"
                {...register('endDate')}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              />
              <p className="text-xs text-gray-500 mt-1">
                If not provided, will be calculated based on program duration
              </p>
              {errors.endDate && (
                <p className="mt-1 text-sm text-red-600">{errors.endDate.message}</p>
              )}
            </div>

            {/* Program Timeline Preview */}
            {watchedStartDate && watchedProgramLength && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <h4 className="text-sm font-medium text-blue-900 mb-2">Program Timeline</h4>
                <div className="text-sm text-blue-800">
                  <div>📅 <strong>Start:</strong> {new Date(watchedStartDate).toLocaleDateString()}</div>
                  <div>🏁 <strong>Expected End:</strong> {calculateEndDate(new Date(watchedStartDate), watchedProgramLength)?.toLocaleDateString()}</div>
                  <div>⏱️ <strong>Duration:</strong> {watchedProgramLength} weeks ({watchedProgramLength * 7} days)</div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Step 2: Schedule and Intensity */}
        {step === 2 && (
          <div className="coach-section space-y-6">
            <h2 className="text-xl font-semibold text-gray-900 flex items-center">
              <Calendar className="w-5 h-5 mr-2" />
              Schedule & Intensity
            </h2>

            {/* Workout Schedule */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Workout Days *
              </label>
              <div className="grid grid-cols-7 gap-2">
                {dayNames.map((day, index) => {
                  const dayNumber = index + 1
                  const isSelected = watchedSchedule?.includes(dayNumber)
                  
                  return (
                    <label
                      key={day}
                      className={`flex flex-col items-center p-3 border rounded-lg cursor-pointer transition-colors ${
                        isSelected
                          ? 'border-blue-500 bg-blue-50 text-blue-700'
                          : 'border-gray-300 hover:border-gray-400'
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={(e) => handleScheduleChange(dayNumber, e.target.checked)}
                        className="sr-only"
                      />
                      <span className="text-sm font-medium">{day}</span>
                    </label>
                  )
                })}
              </div>
              {errors.schedule && (
                <p className="mt-1 text-sm text-red-600">{errors.schedule.message}</p>
              )}
            </div>

            {/* Weekly Sets */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Total Weekly Sets *
              </label>
              <input
                type="number"
                min="10"
                max="200"
                {...register('totalWeeklySets', { valueAsNumber: true })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              />
              <p className="text-xs text-gray-500 mt-1">
                Total number of sets across all workouts per week
              </p>
              {errors.totalWeeklySets && (
                <p className="mt-1 text-sm text-red-600">{errors.totalWeeklySets.message}</p>
              )}
            </div>

            {/* Workout Preferences */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Workout Duration (minutes)
                </label>
                <input
                  type="number"
                  min="30"
                  max="120"
                  {...register('preferences.workoutDuration', { valueAsNumber: true })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Intensity Preference
                </label>
                <select
                  {...register('preferences.intensityPreference')}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                >
                  <option value="low">Low</option>
                  <option value="moderate">Moderate</option>
                  <option value="high">High</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Exercise Variety
                </label>
                <select
                  {...register('preferences.exerciseVariety')}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                >
                  <option value="minimal">Minimal</option>
                  <option value="moderate">Moderate</option>
                  <option value="high">High</option>
                </select>
              </div>
            </div>
          </div>
        )}

        {/* Step 3: Muscle Focus & Customization */}
        {step === 3 && (
          <div className="coach-section space-y-6">
            <h2 className="text-xl font-semibold text-gray-900 flex items-center">
              <Dumbbell className="w-5 h-5 mr-2" />
              Muscle Focus & Customization
            </h2>

            {/* Muscle Activations */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-3">
                Muscle Group Focus
              </label>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {muscleActivations.map((muscle) => (
                  <div key={muscle.id} className="flex items-center justify-between p-3 border border-gray-200 rounded-lg">
                    <div>
                      <div className="font-medium text-gray-900">{muscle.name}</div>
                      <div className="text-sm text-gray-500">{muscle.category}</div>
                    </div>
                    <div className="flex items-center space-x-2">
                      <input
                        type="range"
                        min="0"
                        max="1"
                        step="0.1"
                        value={selectedMuscleActivations[muscle.id] || 0}
                        onChange={(e) => handleMuscleActivationChange(muscle.id, parseFloat(e.target.value))}
                        className="w-20"
                      />
                      <span className="text-sm font-medium w-8">
                        {Math.round((selectedMuscleActivations[muscle.id] || 0) * 100)}%
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Equipment */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Available Equipment
              </label>
              {loadingEquipment ? (
                <div className="flex items-center justify-center p-4">
                  <LoadingSpinner size="sm" />
                  <span className="ml-2 text-sm text-gray-600">Loading equipment...</span>
                </div>
              ) : (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                  {availableEquipment.map((equipment) => {
                    const isSelected = customEquipment.includes(equipment.id as Equipment)
                    return (
                      <label
                        key={equipment.id}
                        className={`flex items-center p-2 border rounded-lg cursor-pointer transition-colors ${
                          isSelected
                            ? 'border-blue-500 bg-blue-50'
                            : 'border-gray-300 hover:border-gray-400'
                        }`}
                      >
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={(e) => {
                            if (e.target.checked) {
                              addCustomEquipment(equipment.id as Equipment)
                            } else {
                              removeCustomEquipment(equipment.id as Equipment)
                            }
                          }}
                          className="sr-only"
                        />
                        <span className="text-sm">{equipment.name}</span>
                      </label>
                    )
                  })}
                </div>
              )}
              {availableEquipment.length === 0 && !loadingEquipment && (
                <p className="text-sm text-gray-500 p-4 text-center">
                  No equipment available. Please add equipment to your library first.
                </p>
              )}
            </div>

            {/* Goals */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Specific Goals
              </label>
              <div className="space-y-2">
                {customGoals.map((goal) => (
                  <div key={goal} className="flex items-center justify-between p-2 bg-gray-50 rounded-lg">
                    <span>{goal}</span>
                    <button
                      type="button"
                      onClick={() => removeCustomGoal(goal)}
                      className="text-red-500 hover:text-red-700"
                    >
                      <Minus className="w-4 h-4" />
                    </button>
                  </div>
                ))}
                <div className="flex gap-2">
                  <input
                    type="text"
                    placeholder="Add a goal..."
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    onKeyPress={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault()
                        const target = e.target as HTMLInputElement
                        addCustomGoal(target.value)
                        target.value = ''
                      }
                    }}
                  />
                  <button
                    type="button"
                    onClick={(e) => {
                      const input = (e.target as HTMLButtonElement).previousElementSibling as HTMLInputElement
                      addCustomGoal(input.value)
                      input.value = ''
                    }}
                    className="px-3 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600"
                  >
                    <Plus className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Navigation */}
        <div className="flex justify-between">
          <div>
            {step > 1 && (
              <button
                type="button"
                onClick={() => setStep(step - 1)}
                className="px-4 py-2 text-gray-600 hover:text-gray-800"
              >
                Previous
              </button>
            )}
          </div>
          
          <div>
            {step < 3 ? (
              <button
                type="button"
                onClick={() => setStep(step + 1)}
                className="coach-button-primary px-6 py-2"
              >
                Next
              </button>
            ) : (
              <div className="space-y-2">
                <button
                  type="submit"
                  disabled={!isValid || isGenerating}
                  className="coach-button-primary px-6 py-2 flex items-center disabled:opacity-50"
                >
                  <Wand2 className="w-4 h-4 mr-2" />
                  Generate Program
                </button>
                

              </div>
            )}
          </div>
        </div>
      </form>
    </div>
  )
}